/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 */
import { Component, ErrorInfo, ReactNode } from 'react'
import Button from './Button'
import Card from './Card'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error, errorInfo)
    }

    // Report to OCS telemetry
    import('@/lib/error-reporter').then(({ reportError: report }) => {
      report(error, { type: 'react_error_boundary', componentStack: errorInfo.componentStack });
    }).catch(() => {});

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <div className="text-center">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-10 h-10 text-error-600" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Something went wrong
              </h1>

              {/* Description */}
              <p className="text-muted-foreground mb-6">
                We're sorry, but something unexpected happened. The error has been logged
                and we'll look into it.
              </p>

              {/* Error details */}
              {this.state.error && (
                <div className="mb-6 text-left">
                  <details className="bg-muted rounded-lg p-4">
                    <summary className="cursor-pointer font-semibold text-sm text-foreground mb-2">
                      Error Details (Development Only)
                    </summary>
                    <div className="mt-2 space-y-2">
                      <div className="text-xs">
                        <strong className="text-error-600">Error:</strong>
                        <pre className="mt-1 p-2 bg-card rounded border border-border overflow-x-auto">
                          {this.state.error.toString()}
                        </pre>
                      </div>
                      {this.state.errorInfo && (
                        <div className="text-xs">
                          <strong className="text-error-600">Component Stack:</strong>
                          <pre className="mt-1 p-2 bg-card rounded border border-border overflow-x-auto text-xs">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={this.handleReset}
                >
                  Try Again
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.location.href = '/'}
                >
                  Go to Home
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </Button>
              </div>

              {/* Contact info */}
              <p className="text-sm text-muted-foreground mt-6">
                If this problem persists, please contact support or file an issue on GitHub.
              </p>
            </div>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
